import { createSelector } from "@reduxjs/toolkit";
import { Timer } from "../../entities/timer";
import { mapToArray } from "../../helpers";
import { AppState } from "../appState";

const selectTimers = (state: AppState) => state.kronosState.timerState.timers;
const selectCurrentTimer = (state: AppState) =>
  state.kronosState.timerState.currentTimer;
const selectTimerSearchMetadata = (state: AppState) =>
  state.kronosState.timerState.searchMetadata;
const selectTimerFilters = (state: AppState) =>
  state.kronosState.timerState.filters;
const selectTimerSearchOpen = (state: AppState) =>
  state.kronosState.timerState.searchOpen;
const selectSelectedTimerID = (state: AppState) =>
  state.kronosState.timerState.selectedTimerID;
const selectStageTimerSelector = (state: AppState) =>
  state.kronosState.timerState.stagedTimer;
const selectTimerLoading = (state: AppState) =>
  state.kronosState.timerState.loading;
const selectTimerFormOpen = (state: AppState) =>
  state.kronosState.timerState.timerFormOpen;

export const timerFormOpenSelector = createSelector(
  [selectTimerFormOpen],
  (s) => s
);

export const currentTimerSelector = createSelector(
  [selectCurrentTimer],
  (s) => s
);

export const timerLoadingSelector = createSelector(
  [selectTimerLoading],
  (s) => s
);

export const timersSelector = createSelector([selectTimers], (timers) =>
  mapToArray(timers).sort((a, b) => b.id - a.id)
);

export const latestUncompletedTimerSelector = createSelector(
  [selectTimers],
  (timers) => {
    const t = mapToArray(timers)
      .filter((t) => !t.completed())
      .sort((a, b) => b.id - a.id);

    if (t.length) return t[0];
    return undefined;
  }
);

export const latestCompletedTimerSelector = createSelector(
  [selectTimers],
  (timers) => {
    const t = mapToArray(timers)
      .filter((t) => t.completed())
      .sort((a, b) => b.id - a.id);

    if (t.length) return t[0];
    return undefined;
  }
);

export const completedTimersSelector = createSelector(
  [selectTimers],
  (timers) =>
    mapToArray(timers)
      .filter((t) => t.completed())
      .sort((a, b) => b.id - a.id)
);

export const selectedTimerSelector = createSelector(
  [selectTimers, selectSelectedTimerID],
  (timers, selectedTimerID) => {
    if (selectedTimerID) return timers[selectedTimerID];
    return undefined;
  }
);

export const stagedTimerSelector = createSelector(
  [selectStageTimerSelector],
  (s) => (s ? new Timer(s) : s)
);

export const selectedTimerIDSelector = createSelector(
  [selectSelectedTimerID],
  (s) => s
);

export const timerSearchOpenSelector = createSelector(
  [selectTimerSearchOpen],
  (s) => s
);

export const timerSearchMetadataSelector = createSelector(
  [selectTimerSearchMetadata],
  (s) => s
);

export const timerFiltersSelector = createSelector(
  [selectTimerFilters],
  (s) => s
);

export const timerSelector = (id?: number) => {
  return createSelector([selectTimers], (timers) => {
    if (id) return timers[id];
    return undefined;
  });
};

export const timersForUserSelector = (userID?: number) => {
  return createSelector([selectTimers], (timers) => {
    return mapToArray(timers)
      .filter((t) => t.userID === userID)
      .sort((a, b) => b.id - a.id);
  });
};

export const timersForProjectSelector = (projectID?: number) => {
  return createSelector([selectTimers], (timers) => {
    return mapToArray(timers)
      .filter((t) => t.projectID === projectID)
      .sort((a, b) => b.id - a.id);
  });
};
